import { pluralize, present } from '@art-suite/art-core-ts';
import { memo, useCallback, useMemo, useState } from 'react';

import { css } from '@emotion/react';
import { getScanNoteCount } from '@kaiizen/kai-pro-api';
import { useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { getGroupsInStandardGroupOrder, getTasksOnScopeReport, ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';

import { useNavigate } from 'react-router';
import { useNavigateToScan, useShowNotes } from '../../../State';
import { LAYOUT2 } from '../../../Styles';
import { ScanNavigatorGroupItem } from './ScanNavigatorGroupItem';
import { ScanNavigatorIconItem } from './ScanNavigatorIconItem';
import { EditIcon, ExitEditIcon } from './ScanNavigatorIcons';
import { ScanNavigatorLayout } from './ScanNavigatorLayout';
import { ScanNavigatorOverviewItem } from './ScanNavigatorOverviewItem';
import { ScanNavigatorSearchBar } from './ScanNavigatorSearchBar';
import { ScanNavigatorSectionHeader } from './ScanNavigatorSectionHeader';

const EDIT_TOGGLE_BUTTON_STYLE = css`
  label: EDIT_TOGGLE_BUTTON_STYLE;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.7;
  }
`;

const ICON_ROW_STYLE = css`
  label: ICON_ROW_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing1}px;
  padding: 0 ${LAYOUT2.spacing2}px;
`;

const EMPTY_GROUPS: ScanGroupOptimized[] = [];
export const ScanNavigator = memo(({
  filter,
  filterRegex,
  group,
  onOpenProjectMeasurements,
  review,
  showAllScanPhotos,
  setFilter,
}: {
  review: boolean;
  filter: string;
  filterRegex?: RegExp;
  group?: ScanGroupOptimized | null;
  onOpenProjectMeasurements?: () => void;
  showAllScanPhotos: boolean;
  setFilter: (filter: string) => void;
}) => {
  const { scanId, scanRoot, scanDataMutators, baselineScanRoot, scanNotes } = useScanEditingContextSlice([
    'scanId',
    'scanRoot',
    'scanDataMutators',
    'baselineScanRoot',
    'scanNotes',
  ]);
  const { showNotesEditor } = useShowNotes();
  const navigateToScan = useNavigateToScan();

  const currentGroupId = group?.id;
  const groups = scanRoot?.groups ? scanRoot?.groups : EMPTY_GROUPS;
  const filterEnabled = present(filterRegex);
  const [editingCategory, setEditingCategory] = useState<string | undefined>(undefined);
  const navigate = useNavigate();

  const photoCount = useMemo(() => scanRoot?.optimized.allPhotoIds.length ?? 0, [scanRoot]);
  const scopedTaskCount = useMemo(() => scanRoot ? getTasksOnScopeReport(scanRoot).length : 0, [scanRoot]);
  const notesCount = getScanNoteCount(scanNotes ?? []);

  const toggleCategoryEdit = (category: string) => {
    setEditingCategory(prev => prev === category ? undefined : category);
  };

  const groupsByTemplateId = useMemo(() => {
    const groupsByTemplateId: Record<string, ScanGroupOptimized[]> = {};
    groups.forEach(group => {
      if (!groupsByTemplateId[group.templateId]) groupsByTemplateId[group.templateId] = [];
      groupsByTemplateId[group.templateId].push(group);
    });
    return groupsByTemplateId;
  }, [groups]);

  const showAllScopedTasks = useCallback(() => {
    if (!scanId) return;
    navigate(`/scan/${scanId}/review`);
  }, [scanId, navigate]);

  const showAllNotes = useCallback(() => showNotesEditor({}), [showNotesEditor]);

  if (!scanRoot?.template || !scanRoot?.groups) return null;

  const { clusterOrder, groupClusters } = getGroupsInStandardGroupOrder(scanRoot.template, scanRoot.groups, filterRegex);

  return (
    <ScanNavigatorLayout
      fixedChildren={
        <>
          <ScanNavigatorSearchBar setFilter={setFilter} filter={filter} />
          <ScanNavigatorOverviewItem
            isSelected={false}
            onSelect={() => onOpenProjectMeasurements?.()}
          />
          <div css={ICON_ROW_STYLE}>
            <ScanNavigatorIconItem icon='task' selected={!!review} count={scopedTaskCount} onClick={showAllScopedTasks} />
            <ScanNavigatorIconItem
              icon='camera'
              selected={!!showAllScanPhotos}
              count={photoCount}
              onClick={() => navigateToScan({ scanId, focusPanel: 'photos' })}
            />
            <ScanNavigatorIconItem icon='notes' selected={false} count={notesCount} onClick={showAllNotes} />
          </div>
        </>
      }
    >
      {clusterOrder.map(category =>
        groupClusters[category].length > 0 && (
          <div key={category}>
            <ScanNavigatorSectionHeader
              title={pluralize(groupClusters[category].length, `${filterEnabled ? 'matching ' : ''}${category} group`)}
              buttons={
                <button
                  css={EDIT_TOGGLE_BUTTON_STYLE}
                  onClick={() => toggleCategoryEdit(category)}
                  aria-label={editingCategory === category ? 'Exit edit mode' : 'Enter edit mode'}
                >
                  {editingCategory === category ? <ExitEditIcon /> : <EditIcon />}
                </button>
              }
            />
            {groupClusters[category].map(group => (
              <ScanNavigatorGroupItem
                baselineGroup={baselineScanRoot?.groupsById[group.id]}
                key={group.id}
                group={group}
                isCurrentGroup={group.id === currentGroupId}
                isEditing={editingCategory === category}
                scanDataMutators={scanDataMutators}
                canDeleteGroup={groupsByTemplateId[group.templateId]?.length > 1}
              />
            ))}
          </div>
        )
      )}
    </ScanNavigatorLayout>
  );
});
